import { Injectable } from '@angular/core';
import { ActivatedRoute, Params, Router, UrlTree } from '@angular/router';
import { ExternalUrlOpenerHelperService } from '@core/services/core/external-url-opener-helper.service';
import { MMobileHelperService } from '@core/services/core/mmobile-helper.service';
import { NavController, Platform } from '@ionic/angular';
import { NavigationOptions } from '@ionic/angular/providers/nav-controller';
import { AllFeatureFlags } from '@models/mmobile';
import { State as ResultState } from '@modules/result/result.page';
import { AuthMirrorService } from './auth/auth-mirror.service';
import { ContextService } from './context.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationHelper {

  // HTML5 History Api does not support functions or Classes passed by state
  resultExtraProps: Partial<ResultState> = {};

  constructor(
    private nav: NavController,
    private router: Router,
    private route: ActivatedRoute,
    private auth: AuthMirrorService,
    private platform: Platform,
    private ctx: ContextService,
    private mmobileHelper: MMobileHelperService,
    private urlExternal: ExternalUrlOpenerHelperService
  ) {}

  goHomePage(opts?: NavigationOptions) {
    if (this.auth.authService.getState().isLogged) {
      return this.newRoot(['client-area', 'home'], opts);
    }
    return this.goLoginPage(this.ctx.isAccessTypeParticular);
  }

  goLoginPage(isParticular: boolean, opts?: NavigationOptions) {
    if (!this.platform.is('cordova') && isParticular) {
      const accessParticularLoginEnabled = this.mmobileHelper.checkIsFeatureEnabled(
        AllFeatureFlags.ACCESS_PARTICULARS_LOGIN
      );
      if (!accessParticularLoginEnabled) {
        const aciParticularsUrl = this.mmobileHelper.getCustomConfigData('URLS', 'ACIPARTICULARS') || '';
        this.urlExternal.openExternalUrl(aciParticularsUrl, null, null,
          {
            ios: '',
            android: '',
            web: { sameTab: true }
          }
        );
        return;
      }
    }

    if (this.ctx.isApp) {
      this.newRoot('/login', opts);
    } else if (isParticular) {
      this.newRoot('login/particulares', opts);
    } else {
      this.newRoot('login/empresas');
    }
  }

  goResultPage({ buttons, links, subdescriptionComponentRef, ...state}: ResultState) {
    this.resultExtraProps = {
      ...(buttons && { buttons }),
      ...(links && { links }),
      ...(subdescriptionComponentRef && { subdescriptionComponentRef })
    };
    this.nav.navigateRoot(['/result'], { state });
  }

  /**
   * @deprecated
   */
  to(path: string | UrlTree | any[], state?) {
    return this.nav.navigateForward(path, { state });
  }

  /**
   * @description
   * Calls Angular navigateForward without State
   * @return Forward navigation promise
   */
  toNew(path: string | UrlTree | any[], opts?: NavigationOptions) {
    return this.nav.navigateForward(path, opts);
  }

  toNewWithoutState(path: string | UrlTree | any[], opts?: NavigationOptions) {
    if (opts) {
      delete opts.state;
    }
    return this.nav.navigateForward(path, opts);
  }

  /**
   * @description
   * Calls Angular navigateBack
   * @return Back navigation promise
   */
  back(path: string, state?) {
    return this.nav.navigateBack(path, { state });
  }

  /**
   * @description
   * Calls Angular navigateBack
   * @return Back navigation promise
   */
  backWithOpt(path: string[] | string, opt?) {
    return this.nav.navigateBack(path, opt );
  }

  /**
   * @description
   * TODO: Check if is viable for use in web navigation
   * Goes back in the context of Ionic's stack navigation
   * Can be used for pop() from globally accessed pages
   * @return Back navigation promise
   */
  pop() {
    const isFirstPage = this.ctx.isFirstPage$.getValue();
    return isFirstPage ? this.goHomePage() : this.nav.pop();
  }

  /**
   * @deprecated
   */
  root(path: string, state?, queryParams?) {
    const opts: NavigationOptions = {
      state,
      queryParams,
      animationDirection: 'back'
    };
    if (this.platform.is('ios')) {
      delete opts.animationDirection;
    }
    return this.nav.navigateRoot(path, opts);
  }

  /**
   * @description
   * Calls Angular navigateRoot
   */
  newRoot(path: string | string[], opts?: NavigationOptions) {
    opts = { ...opts, animationDirection: 'back' };
    if (this.platform.is('ios')) {
      delete opts.animationDirection;
    }
    return this.nav.navigateRoot(path, opts);
  }

  /**
   * @description
   * This method should be used only for modules that exists on Public Area and Private
   * Navigates root to /login or /client-area depending whether user is logged
   */
  cancelProcess() {
    const { isLogged } = this.auth.authService.getState();
    if (isLogged) {
      return this.nav.navigateRoot('client-area/home');
    }
    return this.nav.navigateRoot('login');
  }

  /**
   * @description
   * Calls Angular Router for { paramName }
   * @return Object
   * @return null if not found
   * @return state object for empty calls
   */
  get(paramName?: string) {
    const { extras } = this.router.getCurrentNavigation() || ({} as any);
    let { state } = extras || ({} as any);
    state = state || {};

    return paramName ? state[paramName] : state;
  }

  /**
   * @description
   * Calls Angular Route for { pathName }
   * @return null if not found
   */
  getQueryParams(param?: string) {
    const qparams = this.route.snapshot.queryParams || ({} as Params);
    return param ? qparams[param] : qparams;
  }
}
